/** @jsxRuntime classic */
/** @jsx jsx */

import { css, jsx } from '@emotion/react';
import {
  Colors,
  Icon,
  Link,
  Typography,
  VisuallyHidden,
} from '@zapier/design-system';
import { ServiceIcons } from '@zapier/design-system-patterns';
import { labelStyles } from '@zapier/style-encapsulation';
import { MEDIA_MAX_WIDTH, MEDIA_MIN_WIDTH } from '../../../constants';
import { Service } from '../../../graphql/queries';
import { CTAType, SectionName, useAvroTracking } from '../../../observability';

type Props = {
  isHiddenOnMobile: boolean;
  services: Service[];
  title?: string | null;
  url: string;
};

const Styles = labelStyles('TemplateCard', {
  root: css({
    height: '345px',
    width: '100%',
    flexShrink: 0,
    position: 'relative',
    padding: '32px 32px 16px',
    backgroundColor: Colors.neutral100,
    border: `1px solid ${Colors.neutral800}`,
    borderRadius: '5px',
    boxSizing: 'border-box',
    transition: 'box-shadow 250ms ease-in-out',

    '&:hover, &:focus-within': {
      boxShadow: `6px 6px 0 ${Colors.neutral800}`,
    },

    '& + &': {
      marginTop: '20px',
    },

    '&.hidden-on-mobile': {
      [MEDIA_MAX_WIDTH.small]: {
        visibility: 'hidden',
      },
    },

    [MEDIA_MIN_WIDTH.small]: {
      flexShrink: 1,

      '&:nth-child(3)': {
        display: 'none',
      },

      '& + &': {
        marginTop: 0,
        marginLeft: '30px',
      },
    },

    [MEDIA_MIN_WIDTH.large]: {
      maxWidth: '370px',

      '&:nth-child(3)': {
        display: 'block',
      },
    },
  }),

  title: css({
    ...Typography.pageHeader9,
    minHeight: '128px',
    margin: '24px 0 52px',
  }),

  cta: css({
    ...Typography.paragraph3Bold,
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    color: Colors.zapier,
    textDecoration: 'none',
  }),

  hiddenLink: css({
    outline: 'none',
    position: 'absolute',
    height: '100%',
    width: '100%',
    top: 0,
    left: 0,
  }),
});

export function TemplateCard({
  title,
  isHiddenOnMobile,
  services = [],
  url,
}: Props) {
  const { emitCTAClickEvent } = useAvroTracking();
  return (
    <div
      className={isHiddenOnMobile ? 'hidden-on-mobile' : ''}
      css={Styles.root}
      data-testid="template-card"
    >
      <ServiceIcons
        maxServices={3}
        services={services.map((service) => ({
          name: service.name || '',
          src: service.image || '',
        }))}
        size={60}
      />
      <h3 css={Styles.title}>{title}</h3>
      <div aria-hidden={true} css={Styles.cta}>
        Try it now
        <Icon name="arrowBigRight" />
      </div>
      <Link
        href={url}
        onClick={() =>
          emitCTAClickEvent({
            href: url,
            section: SectionName.PopularTemplatesSection,
            text: 'Try it now',
            type: CTAType.zap_card_link,
          })
        }
      >
        <div
          css={Styles.hiddenLink}
          data-testid={`${title?.replace(' ', '-')}-link`}
        >
          <VisuallyHidden>Try it now</VisuallyHidden>
        </div>
      </Link>
    </div>
  );
}
